import React from 'react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { IHotelDetails } from '@common-types';
import './style.scss';

interface IProps {
  hotel: IHotelDetails;
  refAnchor: React.RefObject<HTMLDivElement>;
}

type SegmentType = 'distance' | 'airport' | 'item';

// The additional descriptions come straight from the backend (content provider),
// so we cannot rely on translation keys. Instead we detect well-known phrases in
// every locale we support to decide how to style each block.
// NOTE: the non-English strings are translations of the same backend phrase. If the
// content provider ever changes its wording, update the matching string here.
const DISTANCE_PHRASES = [
  'distances are displayed to the nearest 0.1 mile and kilometer', // en
  'las distancias se expresan al 0,1 de milla y kilómetro más cercano', // es
  'les distances sont affichées au 0,1 mile et kilomètre le plus proche', // fr
  'le distanze sono visualizzate allo 0,1 di miglio e chilometro più vicino', // it
  'as distâncias são exibidas ao 0,1 de milha e quilômetro mais próximo', // pt
];

const AIRPORT_PHRASES = [
  'the nearest airports are', // en
  'los aeropuertos más cercanos son', // es
  'les aéroports les plus proches sont', // fr
  'gli aeroporti più vicini sono', // it
  'os aeroportos mais próximos são', // pt
];

interface ISegment {
  type: SegmentType;
  html: string;
}

// Strip HTML tags, collapse whitespace and lowercase so phrase matching is robust.
function toPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function classify(html: string): SegmentType {
  const text = toPlainText(html);
  if (DISTANCE_PHRASES.some(phrase => text.startsWith(phrase))) {
    return 'distance';
  }
  return 'item';
}

// A single backend description (e.g. "attractions") can contain both the distances
// intro and the "nearest airports" list. Split it so each part gets its own styling.
function splitDescription(html: string): ISegment[] {
  const lower = html.toLowerCase();

  let airportIndex = -1;
  AIRPORT_PHRASES.forEach(phrase => {
    const index = lower.indexOf(phrase);
    if (index !== -1 && (airportIndex === -1 || index < airportIndex)) {
      airportIndex = index;
    }
  });

  if (airportIndex === -1) {
    return [{ type: classify(html), html }];
  }

  const segments: ISegment[] = [];
  // Drop a dangling opening tag left over from the split point.
  const before = html.slice(0, airportIndex).replace(/<p[^>]*>\s*$/i, '').trim();
  const airport = html.slice(airportIndex);

  if (before) {
    segments.push({ type: classify(before), html: before });
  }
  segments.push({ type: 'airport', html: airport });

  return segments;
}

function getClassName(type: SegmentType): string {
  switch (type) {
    case 'distance':
      return 'additional-description__distance';
    case 'airport':
      return 'additional-description__nearest-airport';
    default:
      return 'additional-description__item';
  }
}

export function AdditionalDescription({ hotel, refAnchor }: IProps): React.ReactNode {
  return hotel.additionalDescriptions?.filter(item => item !== null && item !== undefined).length ? (
      <div ref={refAnchor} className="additional-description">
        {hotel.isVacationRental
          ?
            <div className="rooms-search__list-text secondary-font vacation mb-0">
              <div className="rooms-search__header secondary-font" style={{ fontSize: '24px' }}><FormattedMessage id="additional.descriptions" /></div>
            </div>
          : <h4 className="additional-description__title"><FormattedMessage id="additional.descriptions" /></h4>}

        <div className="additional-description__text-wrapper">
          <div className="additional-description__text secondary-font">
            {hotel.additionalDescriptions
                .filter(item => item !== null && item !== undefined)
                .flatMap((hd, i) => splitDescription(hd).map((segment, j) => (
                  <div key={`${i}-${j}`} className={getClassName(segment.type)}>
                    {segment.type !== 'airport' && (
                      <span className="additional-description__icon">
                        <FontAwesomeIcon icon={segment.type === 'distance' ? faLocationDot : faCircleCheck} />
                      </span>
                    )}
                    {parse(segment.html)}
                  </div>)))}
          </div>
        </div>
      </div>) : null;
}
